// Issued certificates for the authenticated user
// (GET /v1/user/certificate — see Commerce OpenAPI).
// Public verify: GET /v1/commerce/certificate/verify/{publicNumber}.

import { ApiError, request, resolveApiUrl } from "@/lib/api/client";

/** Lifecycle of `commerce_certificate_access` on a user certificate item. */
export type CertificateAccessStatus =
  | "active"
  | "cancelled"
  | "expired"
  | "revoked";

/** Item from GET /v1/user/certificate `data[]`. */
export interface ApiUserCertificateItem {
  id: number;
  issuedAt: string;
  title: string;
  recipientDisplayName: string;
  /** Public document number, e.g. CERT-2026-000001. */
  publicNumber: string;
  /** Signed download URL when access is usable; otherwise null. */
  downloadUrl: string | null;
  /** Signed preview URL when access is usable; otherwise null. */
  previewUrl: string | null;
  /** ISO-8601 end of access; null for forever grants. */
  expiresAt: string | null;
  accessStatus: CertificateAccessStatus | null;
  cancellable: boolean;
}

/**
 * Whether the user can download / preview the certificate.
 * `cancelled` still grants access until `expiresAt` (backend keeps URLs).
 * `expired` / `revoked` (and missing access) do not.
 */
export function hasUsableCertificateAccess(
  item: Pick<ApiUserCertificateItem, "accessStatus"> | null | undefined,
): boolean {
  const status = item?.accessStatus;
  return status === "active" || status === "cancelled";
}

/**
 * `data` from GET /v1/commerce/certificate/verify/{publicNumber}.
 * Always HTTP 200; no owner PII (name, product, userId).
 */
export interface ApiCertificateVerification {
  valid: boolean;
  /** ISO-8601 when `valid` is true; otherwise null. */
  issuedAt: string | null;
}

/** Backend / OpenAPI format: CERT-YYYY-NNNNNN. */
export const CERTIFICATE_PUBLIC_NUMBER_RE = /^CERT-\d{4}-\d{6}$/;

export function normalizeCertificatePublicNumber(raw: string): string {
  return raw.trim().toUpperCase();
}

export function isCertificatePublicNumberFormat(value: string): boolean {
  return CERTIFICATE_PUBLIC_NUMBER_RE.test(normalizeCertificatePublicNumber(value));
}

/** Share in-flight GETs so React Strict Mode / remounts don't double-hit the API. */
const inflight = new Map<string, Promise<unknown>>();

function dedupeInflight<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;
  const promise = run().finally(() => {
    if (inflight.get(key) === promise) inflight.delete(key);
  });
  inflight.set(key, promise);
  return promise;
}

/** GET /v1/user/certificate — list issued certificates (cookie session). */
export async function fetchUserCertificates(): Promise<ApiUserCertificateItem[]> {
  const key = "user-certificate:session";
  return dedupeInflight(key, async () => {
    const data = await request<ApiUserCertificateItem[]>(
      "/v1/user/certificate",
      { method: "GET" },
    );
    return data ?? [];
  });
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1] ?? null;
}

/**
 * GET /v1/commerce/certificate/verify/{publicNumber} — public, no auth.
 * Unknown / malformed / expired / revoked numbers return `{ valid: false, issuedAt: null }`.
 */
export function verifyCertificate(
  publicNumber: string,
): Promise<ApiCertificateVerification> {
  const normalized = normalizeCertificatePublicNumber(publicNumber);
  const key = `certificate-verify:${normalized}`;
  return dedupeInflight(key, () =>
    request<ApiCertificateVerification>(
      `/v1/commerce/certificate/verify/${encodeURIComponent(normalized)}`,
      { method: "GET" },
    ),
  );
}

/** Download an issued certificate PNG via its signed `downloadUrl`. */
export async function downloadUserCertificate(
  downloadUrl: string,
  fallbackFilename: string,
): Promise<void> {
  const url = resolveApiUrl(downloadUrl);
  let res: Response;
  try {
    res = await fetch(url, { headers: { Accept: "image/png,application/octet-stream,*/*" } });
  } catch (err) {
    console.error("Failed to download certificate", err);
    throw new ApiError(0, "NETWORK_ERROR", "Could not reach the server. Check your connection.");
  }

  if (!res.ok) {
    console.error("Certificate download failed", res.status, downloadUrl);
    throw new ApiError(res.status, "DOWNLOAD_FAILED", "Could not download the certificate.");
  }

  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers.get("Content-Disposition")) ??
    fallbackFilename;
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
